import LocationInput, {
  LocationInputHandle,
} from 'components/authorable/shared/site-structure/Header/LocationInput';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Location } from 'components/authorable/shared/site-structure/Header/LocationInput';
import { Field, LinkField } from '@sitecore-content-sdk/nextjs';
import { SearchProvider } from 'lib/utils/coveo/SearchProvider';
import { SearchBox, SearchBoxHandle } from './SearchBox';
import { loadAdvancedSearchQueryActions } from '@coveo/headless';
import { useCoveoEngine } from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import { useCoveoEngineControls } from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { useRouter } from 'next/router';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import { ParsedUrlQuery } from 'node:querystring';

type SearchConfig = {
  searchHub: string;
  pipeline: string;
  accessToken: string;
  organizationId: string;
};

type SearchHeaderWidgetProps = {
  locationSearchPlaceholderText?: Field<string>;
  searchButtonLabel?: string;
  searchTermPlaceholderText?: Field<string>;
  searchLink?: LinkField;
  searchConfig: SearchConfig;
  shouldRedirect?: boolean;
  searchFields?: string[];
  fieldsToInclude?: string[];
  onLocationCoordinatesChange?: (
    coords: {
      latitude: number | null;
      longitude: number | null;
    } | null
  ) => void;
  onSearchTermChange?: (term: string) => void;
  clearOnLoad?: boolean;
  hasCoveoWrapper?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
// Separate component for the search interface
const SearchInterface: React.FC<{
  searchBoxRef: React.RefObject<SearchBoxHandle | null>;
  locationRef: React.RefObject<LocationInputHandle | null>;
  isSearchFocused: boolean;
  setIsSearchFocused: (value: boolean) => void;
  isLocationFocused: boolean;
  setIsLocationFocused: (value: boolean) => void;
  searchBorderColor: string;
  locationBorderColor: string;
  location: Location | null;
  setLocation: (location: Location | null) => void;
  handleSearchClick: () => void;
  hasCoveoWrapper?: boolean;
  props: SearchHeaderWidgetProps;
}> = ({
  searchBoxRef,
  locationRef,
  isSearchFocused,
  setIsSearchFocused,
  isLocationFocused,
  setIsLocationFocused,
  searchBorderColor,
  locationBorderColor,
  location,
  setLocation,
  handleSearchClick,
  hasCoveoWrapper = false,
  props,
}) => {
  return (
    <div className="lg:flex lg:mt-[40px] mt-[32px] gap-5">
      <div className="lg:flex w-full gap rounded-sm ">
        <div
          onFocus={() => setIsSearchFocused(true)}
          onBlur={() => setIsSearchFocused(false)}
          className={`w-full lg:w-1/2  lg:border-b-2 border-b-0 lg:border-r-0 border-r-2 border-2 ${searchBorderColor} rounded-tl-sm rounded-bl-sm lg:rounded-tr-none rounded-tr-sm lg:rounded-br-none transition-colors duration-200`}
        >
          <SearchBox
            ref={searchBoxRef}
            placeholder={props?.searchTermPlaceholderText}
            onSubmitRedirect={false}
            onClear={() => setIsSearchFocused(false)}
            hasCoveoWrapper={hasCoveoWrapper}
          />
        </div>
        <div
          className={`h-[2px] lg:h-auto lg:w-[2px] w-full transition-colors duration-200 ${isSearchFocused || isLocationFocused ? 'bg-blue-base' : 'bg-gray-100'}`}
        ></div>
        <div
          onFocus={() => setIsLocationFocused(true)}
          onBlur={() => setIsLocationFocused(false)}
          className={`w-full lg:w-1/2 border-t-0 lg:border-t-2 lg:border-l-0 border-2 ${locationBorderColor} rounded-tr-sm rounded-br-sm lg:rounded-tl-none rounded-bl-sm lg:rounded-bl-none transition-colors duration-200`}
        >
          <LocationInput
            ref={locationRef}
            value={location ?? undefined}
            onLocationSelect={(loc: Location) => {
              setLocation(loc);
              // Push coordinates up so ProviderSearch updates finalCoordinates
              if (props.onLocationCoordinatesChange) {
                if (loc.lat != null && loc.lng != null) {
                  props.onLocationCoordinatesChange({ latitude: loc.lat, longitude: loc.lng });
                } else {
                  props.onLocationCoordinatesChange(null);
                }
              }
              // New coordinates selected: clear any manual sort override so distance can re-apply
              try {
                if (typeof window !== 'undefined') {
                  window.sessionStorage.removeItem('coveoUserSortOverride');
                }
              } catch {}
            }}
            placeholder={props?.locationSearchPlaceholderText}
            onClear={() => {
              setIsLocationFocused(false);
              setLocation(null);
              // Propagate cleared coordinates upwards
              if (props.onLocationCoordinatesChange) {
                props.onLocationCoordinatesChange(null);
              }
              // Disable distance mode and reset manual override
              try {
                if (typeof window !== 'undefined') {
                  window.sessionStorage.removeItem('coveoUserSortOverride');
                }
              } catch {}
            }}
            onEnter={handleSearchClick}
          />
        </div>
      </div>

      {props.shouldRedirect ? (
        <LinkWrapper
          ctaVariant="primary"
          className="px-12 py-4 font-semibold text-white bg-blue-600 hover:bg-blue-700 mt-5 lg:mt-0 w-full lg:w-fit"
          ctaIcon="search"
          ctaIconAlignment="left"
          field={props?.searchLink}
          onClick={handleSearchClick}
        >
          {props?.searchButtonLabel}
        </LinkWrapper>
      ) : (
        <ButtonWrapper
          ctaVariant="primary"
          className="px-12 py-4 font-semibold text-white bg-blue-600 hover:bg-blue-700 mt-5 lg:mt-0 w-full lg:w-fit"
          ctaIcon="search"
          ctaIconAlignment="left"
          onClick={handleSearchClick}
        >
          {props?.searchButtonLabel}
        </ButtonWrapper>
      )}
    </div>
  );
};

const SearchHeaderWidget = (props: SearchHeaderWidgetProps) => {
  const router = useRouter();
  const targetHref = props.searchLink?.value?.href || '/';
  const currentPath = router.pathname;
  const searchBoxRef = useRef<SearchBoxHandle>(null);
  const locationRef = useRef<LocationInputHandle>(null);
  const engine = useCoveoEngine();
  const engineControls = useCoveoEngineControls();
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isLocationFocused, setIsLocationFocused] = useState(false);
  const [location, setLocation] = useState<Location | null>(null);
  const searchBorderColor = isSearchFocused ? 'border-blue-base' : 'border-gray-100';
  const locationBorderColor = isLocationFocused ? 'border-blue-base' : 'border-gray-100';
  const escapeForCoveo = (str: string = ''): string => {
    return str.replace(/([+\-&|!(){}[\]^"~?:\\/])/g, '\\$1');
  };

  // Populate inputs from URL parameters when router is ready
  useEffect(() => {
    if (!router.isReady || props.clearOnLoad) return;

    const { q, loc, lat, lng } = router.query;

    const qStr = typeof q === 'string' ? q : Array.isArray(q) ? q[0] : '';
    const locStr = typeof loc === 'string' ? loc : Array.isArray(loc) ? loc[0] : '';
    const latStr = typeof lat === 'string' ? lat : Array.isArray(lat) ? lat[0] : '';
    const lngStr = typeof lng === 'string' ? lng : Array.isArray(lng) ? lng[0] : '';

    const latNum = latStr ? parseFloat(latStr) : null;
    const lngNum = lngStr ? parseFloat(lngStr) : null;

    if (qStr) {
      searchBoxRef.current?.setCurrentValue(qStr);
    }

    if (locStr || (latNum !== null && !isNaN(latNum) && lngNum !== null && !isNaN(lngNum))) {
      const locObj: Location = {
        address: locStr,
        lat: latNum && !isNaN(latNum) ? latNum : null,
        lng: lngNum && !isNaN(lngNum) ? lngNum : null,
      };
      setLocation(locObj);
      locationRef.current?.setLocationValue(locObj);
    }
  }, [router.isReady, router.query, props.clearOnLoad]);

  const handleSearchClick = useCallback(() => {
    const searchTerm = searchBoxRef.current?.getCurrentValue()?.trim();
    const location = locationRef.current?.getLocationValue();
    const locationTerm = location?.address?.trim();
    const locationLatitude = location?.lat?.toString();
    const locationLongitude = location?.lng?.toString();

    if (searchTerm && props.onSearchTermChange) {
      props.onSearchTermChange(searchTerm);
    }
    if (props.onLocationCoordinatesChange) {
      if (locationLatitude && locationLongitude) {
        props.onLocationCoordinatesChange({
          latitude: parseFloat(locationLatitude),
          longitude: parseFloat(locationLongitude),
        });
      } else {
        props.onLocationCoordinatesChange(null);
      }
    }

    const updatedQuery: ParsedUrlQuery = { ...router.query };

    if (locationTerm) updatedQuery.loc = locationTerm;
    else delete updatedQuery.loc;

    if (locationLatitude) updatedQuery.lat = locationLatitude;
    else delete updatedQuery.lat;

    if (locationLongitude) updatedQuery.lng = locationLongitude;
    else delete updatedQuery.lng;

    if (searchTerm) updatedQuery.q = searchTerm;
    else delete updatedQuery.q;

    const currentLoc = (router.query.loc as string) || '';
    const currentLat = (router.query.lat as string) || '';
    const currentLng = (router.query.lng as string) || '';
    const currentQ = (router.query.q as string) || '';

    const hasUrlChanged =
      currentLoc !== (locationTerm || '') ||
      currentLat !== (locationLatitude || '') ||
      currentLng !== (locationLongitude || '') ||
      currentQ !== (searchTerm || '');

    if (props.shouldRedirect && targetHref && currentPath !== targetHref) {
      router.push({ pathname: targetHref, query: updatedQuery });
      return;
    }

    if (hasUrlChanged) {
      router.replace({ pathname: router.pathname, query: updatedQuery }, undefined, {
        shallow: true,
      });
    }

    if (!engine) return;

    const { updateAdvancedSearchQueries } = loadAdvancedSearchQueryActions(engine);
    const aqParts: string[] = [];

    // Dynamically build searchTerm part of the AQ
    if (searchTerm && props.searchFields?.length) {
      const escapedSearchTerm = escapeForCoveo(searchTerm);
      const searchConditions = props.searchFields.map((field) => `@${field}*${escapedSearchTerm}*`);
      aqParts.push(`(${searchConditions.join(' OR ')})`);
    }

    // Combine with AND when both exist
    const finalCQ = aqParts.join(' AND ');

    // Update constant query (cq) and ensure aq is reset to prevent stale filters
    engine.dispatch(
      updateAdvancedSearchQueries({
        cq: finalCQ || '',
        aq: '',
      })
    );

    // Apply sort rules based on presence of coordinates
    if (locationLatitude && locationLongitude) {
      // New coordinates: enable distance mode and clear manual override
      try {
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem('coveoUserSortOverride');
        }
      } catch {}
      engineControls?.setDistanceOverride('on');
      engine.dispatch({
        type: 'sort/updateSortCriterion',
        payload: { by: 'field', field: 'distanceinmiles', direction: 'ascending' },
      });
    } else {
      // No coordinates: disable distance mode, reset to default (relevancy)
      try {
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem('coveoUserSortOverride');
        }
      } catch {}
      engineControls?.setDistanceOverride('off');
      engine.dispatch({
        type: 'sort/updateSortCriterion',
        payload: { by: 'relevance' },
      });
    }

    // Trigger search execution via SearchBox controller
    searchBoxRef.current?.submit();
  }, [currentPath, engine, engineControls, props, router, targetHref]);

  const searchInterfaceProps = {
    searchBoxRef,
    locationRef,
    isSearchFocused,
    setIsSearchFocused,
    isLocationFocused,
    setIsLocationFocused,
    searchBorderColor,
    locationBorderColor,
    location,
    setLocation,
    handleSearchClick,
    props,
  };

  return (
    <>
      {props.hasCoveoWrapper ? (
        <SearchInterface {...searchInterfaceProps} hasCoveoWrapper={props.hasCoveoWrapper} />
      ) : (
        <SearchProvider
          searchHub={props?.searchConfig?.searchHub}
          pipeline={props?.searchConfig?.pipeline}
          accessToken={props?.searchConfig?.accessToken}
          organizationId={props?.searchConfig?.organizationId}
          analyticsEnabled={false}
        >
          <SearchInterface {...searchInterfaceProps} hasCoveoWrapper={props.hasCoveoWrapper} />
        </SearchProvider>
      )}
    </>
  );
};

export default SearchHeaderWidget;
